import { Link } from "react-router-dom";
import { Compass } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES } from "@/lib/images";

const SECTIONS = [
  {
    id: "hawk-mountain",
    title: "Hawk Mountain Sanctuary & the Appalachian Trail",
    img: IMAGES.newView08,
    body: [
      "We sit about 11 minutes (roughly 7 miles) from both Hawk Mountain Sanctuary and direct Appalachian Trail access — one of the closest lodgings to either. Each offers hiking for every skill level; Hawk Mountain hosts events through the year and is famous for its autumn raptor migration.",
      "If you're through-hiking the AT, we're the closest place to FMSS LocID 77159 / Asset-ID 1599669 and are glad to serve as a few days of respite from the trail.",
    ],
  },
  {
    id: "dining",
    title: "Dining Nearby — From PA Dutch to Fine Cuisine",
    img: IMAGES.mlsRedBarn,
    body: [
      "You won't go hungry around here. A few places worth knowing about: Deitsch Eck for honest Pennsylvania Dutch food. CJ Hummel's Restaurant for solid, comforting food and country line dancing on select nights — worth timing a visit around. Dietrich's Meats & Country Store for smoked meats, sausages, and the kind of stop that turns into a small pantry haul. Curious Goods for seasonal, locally-sourced cuisine that punches well above the township's weight. Wanamakers General Store for a sandwich, a snack, and the kind of general-store browsing that's mostly disappeared elsewhere.",
      "Ask us when you arrive — we'll share what's been good lately and what's seasonal.",
    ],
  },
  {
    id: "culture",
    title: "Local Culture & Events",
    img: IMAGES.newView11,
    body: [
      "Albany Township and Berks County are quietly full of things to do — Leaser Lake, Crystal Cave, the Berks County Hex Sign Tour, the Albany Township Historical Society, the Jonathan Bond Art Gallery, and the WK&S Steam Train among them.",
      "Annual events to plan around: the Kutztown Folk Festival (late June / early July), the Taste of Hamburg-er Festival (late summer), and the Hawk Mountain Arts Tour. Worth checking dates if you're booking ahead.",
    ],
  },
  {
    id: "wineries",
    title: "Wineries, Breweries & Distilleries",
    img: IMAGES.mlsMistyHills,
    body: [
      "Iron Talon Farm sits in the middle of one of the densest concentrations of small wineries, breweries, and distilleries in eastern Pennsylvania — more than a dozen within a short drive, across Kempton, Hamburg, Kutztown, New Tripoli, Breinigsville, and the greater Lehigh Valley and Berks County.",
      "A few solid anchors to start with: Stony Run (Kempton vineyard and a Breinigsville tasting room) · Hawk Mountain Distillery (Breinigsville, shared with Stony Run) · Eight Oaks Distillery (New Tripoli) · 1787 and Copperz (Hamburg) · Saucony Creek (Kutztown) · Ridgeview (New Tripoli) · Maple Springs, Weathered Vineyards, and Folino Estate, all close.",
      "For planning a tasting tour, we recommend tastingandtours.com and a downloaded Uber app. Many guests build a half-day route between three or four spots and end back here for a fire pit.",
    ],
  },
  {
    id: "shoppes",
    title: "Shoppes & Small Finds",
    img: IMAGES.mainHouseExterior,
    body: [
      "Kempton and the surrounding small towns have a handful of quaint stores worth wandering through. For a mix of food, gifts, and local flavor, Wanamakers General Store and Dietrich's Meats & Country Store are easy stops to work into your day. For something truly one-of-a-kind, visit The Country Seat or Claypoole Hex Signs.",
      "A short drive away, Hamburg offers a few more places to browse, shop, and stretch your legs. Cabela's is a favorite stop for outdoor gear, hunting and fishing supplies, and its large indoor displays. Downtown Hamburg also has small shops, local dining, and the Art & Craft Gallery of Hamburg for guests who enjoy slower wandering and handmade finds.",
    ],
  },
];

const DRIVE_TIMES = [
  { place: "Hawk Mountain Sanctuary", time: "11 min" },
  { place: "Appalachian Trail access", time: "15 min" },
  { place: "Hamburg", time: "17 min" },
  { place: "Kutztown", time: "20 min" },
  { place: "Allentown / Lehigh Valley (ABE airport)", time: "30 min" },
  { place: "Philadelphia (PHL airport)", time: "1 hr 15 min" },
  { place: "Harrisburg (MDT airport)", time: "1 hr 15 min" },
  { place: "New York City (EWR / JFK airports)", time: "2 hr" },
];

export default function LocalGuide() {
  return (
    <div data-testid="local-guide-page">
      <section className="container-x pt-12 lg:pt-24 pb-8" data-testid="local-guide-hero">
        <FadeIn><p className="overline mb-5">Local Guide</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            What's nearby —
            <br />
            <em className="not-italic text-clay">and worth the drive.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            A casual guide to the wineries, restaurants, trails, festivals, and small shoppes
            that make this corner of Pennsylvania feel like its own quiet world. Curated from
            our own family favorites — and tested by years of guests.
          </p>
        </FadeIn>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="local-guide-skyline-divider" />

      {/* DRIVE TIMES */}
      <section className="bg-khaki py-16 lg:py-20" data-testid="local-drive-times">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <FadeIn className="lg:col-span-4">
            <div className="flex gap-4 items-start">
              <Compass size={28} strokeWidth={1.2} className="text-clay flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">Drive Times</p>
                <h2 className="font-serif text-3xl lg:text-4xl text-moss font-light leading-tight tracking-tight">
                  How far is it, really?
                </h2>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={120} className="lg:col-span-8">
            <ul className="divide-y divide-coal/10 border-t border-b border-coal/10">
              {DRIVE_TIMES.map((d) => (
                <li key={d.place} className="flex justify-between py-3 text-coal/80 text-base lg:text-lg">
                  <span>{d.place}</span>
                  <span className="text-clay font-medium">{d.time}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* SECTIONS */}
      {SECTIONS.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`${i % 2 === 0 ? "" : "bg-oat"} py-24 lg:py-28`}
          data-testid={`local-${s.id}`}
        >
          <div className={`container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center ${i % 2 === 0 ? "" : "lg:[&>*:first-child]:order-2"}`}>
            <FadeIn className="lg:col-span-6">
              <div className="aspect-[5/4] overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
              </div>
            </FadeIn>
            <FadeIn delay={120} className="lg:col-span-6">
              <p className="overline mb-4">0{i + 1}</p>
              <h2 className="font-serif text-3xl lg:text-5xl text-moss font-light leading-tight tracking-tight">
                {s.title}
              </h2>
              <div className="mt-6 space-y-4 text-coal/80 text-base lg:text-lg leading-[1.85]">
                {s.body.map((p, k) => (
                  <p key={k}>{p}</p>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>
      ))}

      {/* CLOSING CTA */}
      <section className="bg-ink text-cream py-24 lg:py-32" data-testid="local-cta">
        <div className="container-narrow text-center">
          <FadeIn>
            <p className="overline text-cream/50 mb-5">Ready when you are</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Make a weekend of it.
            </h2>
          </FadeIn>
          <FadeIn delay={120}>
            <p className="mt-8 text-cream/75 text-lg leading-[1.85] max-w-xl mx-auto">
              Stay in one of the cottages, set out for a tasting tour, hike the AT, and end
              the day around a fire pit with cattails rustling somewhere in the dark.
            </p>
          </FadeIn>
          <FadeIn delay={240}>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/stay" className="btn-primary-light" data-testid="local-stay-btn">
                Reserve a cottage
              </Link>
              <Link to="/visit" className="btn-ghost-light" data-testid="local-contact-btn">
                Ask a question
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
